import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { EyeFilled, EyeInvisibleOutlined } from '@ant-design/icons';
import { useResetPassword, useResetPasswordValidateToken, validatePassword } from 'all-in-one-shared-api';
import { revalidateAntdFormLanguageChanged } from '../../i18n';
import forgotIcon from 'assets/images/reset-icon.svg';
import generateKey from 'utils/generateKey';

function ResetPasswordPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [token, setToken] = React.useState('');
  const [validateTokenKey, setValidateTokenKey] = React.useState('');
  const [validToken, setValidToken] = React.useState(false);
  const {
    shouldFetch: shouldValidateToken,
    data: validateTokenData,
    error: validateTokenError,
  } = useResetPasswordValidateToken(validateTokenKey, token, { shouldRetryOnError: false });

  const [newPassword, setNewPassword] = React.useState('');
  const [openAlert, setOpenAlert] = React.useState(false);
  const [snackbarState, setSnackbarState] = React.useState<'success' | 'error'>('success');
  const [snackbarMessageKey, setSnackbarMessageKey] = React.useState('');
  const navigate = useNavigate();
  const errorMapping: Record<string, string> = {
    'auth.password.history.exist': 'auth.password.history.exist',
    'auth.worst.password': 'auth.password.worst',
  };
  const [form] = Form.useForm();

  useEffect(() => {
    revalidateAntdFormLanguageChanged(form);
  }, [form, i18n.language]);
  const {
    shouldFetch: shouldResetPassword,
    isLoading: resetPasswordIsLoading,
    mutate: resetPasswordMutate,
  } = useResetPassword('useResetPassword', token, newPassword, {
    onSuccess: (data) => {
      shouldResetPassword(false);
      if (data?.success) {
        setSnackbarState('success');
        setSnackbarMessageKey('resetPassword.successMessage');
        navigate('/forgot/success', { state: 'password-reset' });
      }
      setOpenAlert(true);
    },
    onError: (err) => {
      shouldResetPassword(false);

      let error = err.message;
      if (errorMapping[err.code]) {
        setSnackbarState('error');
        error = errorMapping[err.code];
      }
      setSnackbarMessageKey(error);
      setOpenAlert(true);
    },
    shouldRetryOnError: false,
  });

  useEffect(() => {
    const tokenFromUrl = searchParams.get('t');
    setToken(tokenFromUrl ?? '');
    setValidateTokenKey(generateKey());
    shouldValidateToken(true);
  }, [searchParams, shouldValidateToken]);

  useEffect(() => {
    if (validateTokenData) {
      setValidToken(validateTokenData !== undefined && validateTokenData.success);
      shouldValidateToken(false);
    }
    if (validateTokenError) {
      setSnackbarState('error');
      setSnackbarMessageKey('resetPassword.expired');
      setOpenAlert(true);
      shouldValidateToken(false);
    }
  }, [shouldValidateToken, t, validateTokenData, validateTokenError]);

  const onFinish = (values: { password: string }) => {
    setNewPassword(values.password ?? '');
    resetPasswordMutate();
    shouldResetPassword(true);
    // resetPasswordMutate();
  };

  const handleCloseSnackbar = () => {
    setOpenAlert(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <img src={forgotIcon} alt="forgot-icon" style={{ marginBottom: 12 }} />
      <Typography.Title level={3}>{t('resetPassword.descriptionText')}</Typography.Title>
      <Form form={form} layout={'vertical'} onFinish={onFinish} requiredMark="optional" validateTrigger={false}>
        <Card style={{ borderRadius: 8, padding: '0 24px', marginTop: 16 }}>
          {openAlert && (
            <Alert
              message={t(snackbarMessageKey)}
              type={snackbarState}
              closable
              onClose={handleCloseSnackbar}
              style={{ marginBottom: 16 }}
            />
          )}
          <Form.Item
            label={t('resetPassword.passwordLabel')}
            name="password"
            hasFeedback
            rules={[
              { required: true, message: t('registration.error.required') },
              () => ({
                validator(_, value) {
                  const validationResult = validatePassword(value);

                  if (!value || validationResult.isValid) {
                    return Promise.resolve();
                  }

                  return Promise.reject(new Error(t('resetPassword.error.invalidPassword')));
                },
              }),
            ]}
            style={{ marginBottom: 12 }}
          >
            <Input.Password
              size="middle"
              placeholder={t('resetPassword.passwordPlaceholder')}
              iconRender={(visible) => (visible ? <EyeFilled /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>
          <Form.Item
            label={t('resetPassword.confirmPasswordLabel')}
            name="confirmPassword"
            hasFeedback
            rules={[
              {
                required: true,
                message: t('registration.error.required'),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('registration.error.invalidConfirmPassword')));
                },
              }),
            ]}
            style={{ marginBottom: 12 }}
          >
            <Input.Password
              size="middle"
              placeholder={t('resetPassword.confirmPasswordPlaceholder')}
              iconRender={(visible) => (visible ? <EyeFilled /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>
        </Card>
        <Form.Item>
          <Button
            block
            type="primary"
            size="large"
            htmlType="submit"
            style={{ marginTop: 16 }}
            disabled={!validToken}
            loading={!resetPasswordIsLoading}
          >
            {t('resetPassword.resetPasswordButton')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}

export default ResetPasswordPage;
